/**
 * Block ID Matching Utility
 * 
 * Provides consistent block ID matching across the application
 * Handles different ID types (string, number) and formats
 */

/**
 * Match block ID with flexible type handling
 * @param {string|number} blockId - Block ID to match
 * @param {string|number} targetId - Target ID to match against
 * @returns {boolean} True if IDs match
 */
export const matchBlockId = (blockId, targetId) => {
  if (blockId === targetId) return true;
  if (String(blockId) === String(targetId)) return true;
  if (Number(blockId) === Number(targetId)) return true;
  return false;
};

/**
 * Find block by ID with flexible matching
 * @param {Array} blocks - Array of blocks
 * @param {string|number} blockId - Block ID to find
 * @returns {Object|null} Found block or null
 */
export const findBlockById = (blocks, blockId) => {
  if (!blocks || !Array.isArray(blocks) || !blockId) {
    return null;
  }
  
  return blocks.find(block => matchBlockId(block.id, blockId)) || null;
};

/**
 * Find block index by ID with flexible matching
 * @param {Array} blocks - Array of blocks
 * @param {string|number} blockId - Block ID to find
 * @returns {number} Block index or -1 if not found
 */
export const findBlockIndex = (blocks, blockId) => {
  if (!blocks || !Array.isArray(blocks) || !blockId) {
    return -1;
  }
  
  return blocks.findIndex(block => matchBlockId(block.id, blockId));
};

/**
 * Extract block ID from DOM element ID
 * Handles different ID formats: "block-content-{id}", "block-playback-{id}", "block-{id}"
 * @param {string} elementId - DOM element ID
 * @returns {string|null} Extracted block ID or null
 */
export const extractBlockIdFromElement = (elementId) => {
  if (!elementId || typeof elementId !== 'string') {
    return null;
  }
  
  return elementId
    .replace('block-content-', '')
    .replace('block-playback-', '')
    .replace('block-', '');
};

/**
 * Find block element in DOM
 * @param {Element} element - Starting element
 * @returns {Element|null} Found block element or null
 */
export const findBlockElement = (element) => {
  if (!element) return null;
  
  return element.closest('[id^="block-content-"]') || 
         element.closest('[id^="block-playback-"]') ||
         element.closest('[id^="block-"]');
};

/**
 * Get character offsets of a selection range within a block element.
 * Selection must be entirely within the block (single-block selection).
 * @param {Element} blockElement - Block content element (e.g. with id block-content-*)
 * @param {Range} range - DOM Range of the selection
 * @returns {{ startOffset: number, endOffset: number }|null} Offsets in block text, or null if invalid
 */
export const getSelectionCharacterOffsets = (blockElement, range) => {
  if (!blockElement || !range) return null;
  const startContainer = range.startContainer;
  const endContainer = range.endContainer;
  const startInBlock = blockElement.contains(startContainer) || blockElement === startContainer;
  const endInBlock = blockElement.contains(endContainer) || blockElement === endContainer;
  if (!startInBlock || !endInBlock) return null;
  try {
    const rStart = document.createRange();
    rStart.setStart(blockElement, 0);
    rStart.setEnd(startContainer, range.startOffset);
    const startOffset = rStart.toString().length;
    const rEnd = document.createRange();
    rEnd.setStart(blockElement, 0);
    rEnd.setEnd(endContainer, range.endOffset);
    const endOffset = rEnd.toString().length;
    if (startOffset > endOffset) return null;
    return { startOffset, endOffset };
  } catch (e) {
    return null;
  }
};

export default {
  matchBlockId,
  findBlockById,
  findBlockIndex,
  extractBlockIdFromElement,
  findBlockElement,
  getSelectionCharacterOffsets,
};

